/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 10/10/23
 */

import { useContext, useEffect, useState } from 'react';
import { WorkplanContext } from '@/components/contexts/WorkplanContext/WorkplanProvider';

import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import { WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';
import { FormType } from '@/components/contexts/FormsContext/FormsProvider';

export type Category = {
    id: number;
    label: string;
    addLabel: string;
    urlLabel: string;
    endpoint: string;
    enabled: boolean;
    template: keyof Workplan;
    form: FormType;
    comments: WorkplanCommentSection;
    helpText: string;
};

const categories: Category[] = [
    {
        id: 5,
        label: 'Positive Conduct and Behaviour',
        addLabel: '',
        urlLabel: '',
        endpoint: '',
        enabled: true,
        template: 'behaviouralFormTemplateId',
        form: FormType.BEHAVIOURAL,
        comments: WorkplanCommentSection.BEHAVIOURAL,
        helpText: 'This is a mandatory goal for all UTS Staff',
    },
    {
        id: 0,
        label: 'Goals',
        addLabel: 'Goal',
        urlLabel: 'goal',
        endpoint: 'career-goal',
        enabled: true,
        template: 'goalFormTemplateId',
        form: FormType.PERSONAL_GOAL,
        comments: WorkplanCommentSection.PERSONAL_GOAL,
        helpText:
            'Goals entered into your workplan should be SMART and reflect the work you plan to do throughout the work planning period in addition to the skills and competencies you wish to improve.',
    },
    {
        id: 1,
        label: 'Activities',
        addLabel: 'Activity',
        urlLabel: 'activity',
        endpoint: 'activity',
        enabled: true,
        template: 'activityFormTemplateId',
        form: FormType.ACTIVITY,
        comments: WorkplanCommentSection.ACTIVITY,
        helpText:
            'Activity related goals should be SMART and reflect the work you plan to do throughout the work planning period.',
    },
    {
        id: 2,
        label: 'Planned Leave',
        addLabel: 'Planned Leave',
        urlLabel: 'planned-leave',
        endpoint: 'leave',
        enabled: true,
        template: 'leaveFormTemplateId',
        form: FormType.LEAVE,
        comments: WorkplanCommentSection.LEAVE,
        helpText:
            'Planned Leave refers to any leave entered within this tool and is considered indicative for the purposes of a leave planning conversation. You should discuss your leave plan with your supervisor for the workplan period and apply for this leave in Ascender Pay.',
    },
    {
        id: 3,
        label: 'Training',
        addLabel: 'Training',
        urlLabel: 'training',
        endpoint: 'health-and-safety-training',
        enabled: true,
        template: 'leaveFormTemplateId',
        form: FormType.HEALTH_AND_SAFETY_TRAINING,
        comments: WorkplanCommentSection.HEALTH_AND_SAFETY_TRAINING,
        helpText:
            'Training items entered in the tool are an indication of the Neo training items that you will complete during the work planning year. Supervisors and Faculty staff should provide staff with a list of the items that need to be complete for each calendar year until further integrations are developed.',
    },
    {
        id: 4,
        label: 'Other Considerations',
        addLabel: 'Consideration',
        urlLabel: 'consideration',
        endpoint: 'consideration',
        enabled: true,
        template: 'considerationFormTemplateId',
        form: FormType.CONSIDERATION,
        comments: WorkplanCommentSection.CONSIDERATION,
        helpText:
            'This section allows you to enter in any additional information that may relevant for your supervisor to take into account when reviewing and approving your workplan.',
    },
];

export const useWorkplanCategories = () => {
    const { workplan } = useContext(WorkplanContext);

    const [cats, set] = useState<Category[]>([]);

    useEffect(() => {
        set(categories.filter((c) => workplan && workplan[c.template]));
    }, [categories, workplan]);

    return cats;
};
